import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrums from "../../common/breadcrumbs";
import { BookingDetails, BookingHistory } from "../../services/puja.services";
import Loader from "../../common/loader";
import { formateDateWithMonth } from "../../../../utils/commonfunction";
import moment from "moment";

const PujaBookingView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [historyList, setHistoryList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const breadcrumbs = [
        { title: "Dashboard", url: "/admin/dashboard" },
        { title: "Puja Bookings", url: "/admin/puja-booking/list/1" },
        { title: "View Booking", url: "" },
    ];

    useEffect(() => {
        if (id) {
            Promise.all([
                BookingDetails(id),
                BookingHistory(id),
            ])
                .then(([detailsRes, historyRes]) => {
                    if (detailsRes.success && detailsRes.data) {
                        setData(detailsRes.data);
                    }
                    setHistoryList(historyRes?.data?.list || []);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log("Error fetching booking details:", error);
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [id]);

    if (isLoading) {
        return <Loader />;
    }

    if (!data) {
        return <div className="text-center mt-5">Puja Booking not found</div>;
    }

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        return `${process.env.REACT_APP_API_URL || ""}/${imagePath}`;
    };

    const currencySymbol = data.currency_type === "USD" ? "$" : "₹";

    const capitalizeFirst = (value) => {
        if (!value) return "N/A";
        return value.charAt(0).toUpperCase() + value.slice(1);
    };

    const formatJoinAt = (date) => {
        if (!date) return "N/A";
        return moment(date).format("DD MMM YYYY, HH:mm:ss");
    };

    let rawPaymentMethod = data?.Payment?.payment_method || data?.payment_method || "";
    let rawPaymentStatus = data?.Payment?.payment_status || data?.payment_status || "";
    const transactionId = data?.Payment?.transaction_id || data?.transaction_id || "N/A";

    if (!rawPaymentMethod && !data?.Payment) {
        rawPaymentMethod = "wallet";
        rawPaymentStatus = "SUCCESS";
    }

    const displayPaymentMethod = rawPaymentMethod.toLowerCase() === 'razorpay' ? 'Razorpay' 
                               : rawPaymentMethod.toLowerCase() === 'wallet' ? 'Wallet' 
                               : (rawPaymentMethod || "N/A");

    const showTransactionId = rawPaymentMethod.toLowerCase() !== 'wallet';

    const getCertificateUrl = (bookingId, isDownload = false) => {
        let baseUrl = process.env.REACT_APP_API_URL || "http://localhost:6500";
        baseUrl = baseUrl.replace(/\/webservice\/api\/v1\/?$/, "");
        const downloadParam = isDownload ? "?download=true" : "";
        return `${baseUrl}/webservice/api/v1/admin/puja/puja-booking/certificate/${bookingId}${downloadParam}`;
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        {/* Header Section */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label mb-0">
                              Puja Booking Details {data.order_number ? `(${data.order_number})` : `(ID: #${data.id})`}
                            </h6>
                            <div>
                                {data.booking_status?.toLowerCase() === "completed" && (
                                    <>
                                        <a
                                            href={getCertificateUrl(data.id, false)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-danger me-2"
                                            title="View Certificate PDF"
                                        >
                                            <i className="fa fa-file-pdf-o me-1"></i> View Certificate PDF
                                        </a>
                                        <a
                                            href={getCertificateUrl(data.id, true)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-success me-2"
                                            title="Download Certificate PDF"
                                        >
                                            <i className="fa fa-download me-1"></i> Download
                                        </a>
                                    </>
                                )}
                                {data.invoice_pdf && (
                                    <a
                                        href={data.invoice_pdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary me-2"
                                        title="View Invoice PDF"
                                    >
                                        <i className="fa fa-file-pdf-o me-1"></i> View Invoice PDF
                                    </a>
                                )}
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                            </div>
                        </div>

                        {/* Top Info Banner */}
                        <div className="alert alert-light border d-flex justify-content-between align-items-center flex-wrap p-3 mb-4">
                            <div className="mb-2 mb-md-0 me-3">
                                <span className="text-muted">Order Number: </span>
                                <strong className="text-primary">{data.order_number || `ORD-PUJA-${data.id}`}</strong>
                            </div>
                            <div className="mb-2 mb-md-0 me-3">
                                <span className="text-muted">Booking Date & Time: </span>
                                <strong>
                                    {data.booking_date ? (
                                        <>
                                            {formateDateWithMonth(data.booking_date)} at {data.booking_time || "-"}
                                        </>
                                    ) : (
                                        <span className="text-muted">Not Scheduled Yet</span>
                                    )}
                                </strong>
                            </div>
                            <div>
                                <span className="text-muted me-2">Status: </span>
                                <span
                                    className={`badge bg-${data.booking_status?.toLowerCase() === "completed"
                                        ? "success"
                                        : data.booking_status?.toLowerCase() === "cancelled"
                                            ? "danger"
                                            : "warning"
                                        } text-capitalize`}
                                    style={{ fontSize: "14px", padding: "6px 12px" }}
                                >
                                    {data.booking_status}
                                </span>
                            </div>
                        </div>

                        <div className="row">
                            {/* Left Side: Puja & Astrologer details */}
                            <div className="col-md-6 mb-4">
                                <div className="card border p-3 mb-4 h-100">
                                    <h6 className="card-title border-bottom pb-2 mb-3 text-primary">
                                        <i className="fa fa-om me-2"></i>Puja Information
                                    </h6>
                                    {data.puja?.puja_image && (
                                        <div className="mb-3 text-center">
                                            <img
                                                src={getImageUrl(data.puja.puja_image)}
                                                alt={data.puja.title}
                                                style={{
                                                    maxWidth: "100%",
                                                    height: "150px",
                                                    borderRadius: "8px",
                                                    objectFit: "cover"
                                                }}
                                            />
                                        </div>
                                    )}
                                    <table className="table table-sm table-borderless">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: "35%" }}>Puja Name</th>
                                                <td>{data.puja?.title || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th>Mode</th>
                                                <td>
                                                    <span className={`badge ${data.puja_mode === "online" ? "bg-info" : "bg-dark"}`}>
                                                        {data.puja_mode === "online" ? "Online" : "In Person"}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Description</th>
                                                <td>{data.puja?.description || "N/A"}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Right Side: User & Payment details */}
                            <div className="col-md-6 mb-4">
                                <div className="card border p-3 mb-4 h-100">
                                    <h6 className="card-title border-bottom pb-2 mb-3 text-success">
                                        <i className="fa fa-user me-2"></i>Customer & Astrologer
                                    </h6>
                                    <table className="table table-sm table-borderless mb-4">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: "35%" }}>User</th>
                                                <td>{data.user?.name || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th>Astrologer</th>
                                                <td>{data.astrologer?.name || "Not Assigned"}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <h6 className="card-title border-bottom pb-2 mb-3 text-warning">
                                        <i className="fa fa-credit-card me-2"></i>Payment Details
                                    </h6>
                                    <table className="table table-sm table-borderless">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: "35%" }}>Amount</th>
                                                <td className="fw-bold text-dark">
                                                    {currencySymbol}{data.amount}
                                                </td>
                                            </tr>
                                            {data.gst_percent !== undefined && (
                                                <>
                                                    <tr>
                                                        <th>GST (%)</th>
                                                        <td>{data.gst_percent || 0}%</td>
                                                    </tr>
                                                    <tr>
                                                        <th>GST Amount</th>
                                                        <td>{currencySymbol}{data.gst_amount || 0}</td>
                                                    </tr>
                                                </>
                                            )}
                                            <tr>
                                                <th>Currency Type</th>
                                                <td>{data.currency_type}</td>
                                            </tr>
                                            {(displayPaymentMethod !== "N/A") && (
                                                <>
                                                    <tr>
                                                        <th>Payment Method</th>
                                                        <td>{displayPaymentMethod}</td>
                                                    </tr>
                                                    {showTransactionId && (
                                                        <tr>
                                                            <th>Transaction ID</th>
                                                            <td><code>{transactionId}</code></td>
                                                        </tr>
                                                    )}
                                                    <tr>
                                                        <th>Payment Status</th>
                                                        <td>
                                                            <span className={`badge ${rawPaymentStatus === "SUCCESS" || rawPaymentStatus === "Completed" ? "bg-success" : "bg-danger"}`}>
                                                                {rawPaymentStatus}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Address details if in_person */}
                            {data.puja_mode === "in_person" && (
                                <div className="col-12 mb-4">
                                    <div className="card border p-3">
                                        <h6 className="card-title border-bottom pb-2 mb-3 text-info">
                                            <i className="fa fa-map-marker-alt me-2"></i>Venue / Address Details
                                        </h6>
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: "15%" }}>City</th>
                                                    <td>{data.city || "N/A"}</td>
                                                </tr>
                                                <tr>
                                                    <th>State</th>
                                                    <td>{data.state || "N/A"}</td>
                                                </tr>
                                                <tr>
                                                    <th>Pincode</th>
                                                    <td>{data.pincode || "N/A"}</td>
                                                </tr>
                                                <tr>
                                                    <th>Full Address</th>
                                                    <td>{data.address || "N/A"}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div className="col-12 mb-2">
                                <div className="card border p-3">
                                    <h6 className="card-title border-bottom pb-2 mb-3 text-primary">
                                        <i className="fa fa-history me-2"></i>Session History
                                    </h6>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm mb-0">
                                            <thead>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Name</th>
                                                    <th>Role</th>
                                                    <th>Session Mode</th>
                                                    <th>IP Address</th>
                                                    <th>Join At</th>
                                                    <th>Connected Seconds</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyList.length ? (
                                                    historyList.map((row, i) => (
                                                        <tr key={row.id || i}>
                                                            <td>{i + 1}</td>
                                                            <td className="text-capitalize">
                                                                {row.role === "astrologer"
                                                                    ? (row.astrologer?.name || row.astrologer?.screen_name || "N/A")
                                                                    : (row.user?.name || "N/A")}
                                                            </td>
                                                            <td>{capitalizeFirst(row.role)}</td>
                                                            <td>{capitalizeFirst(row.session_mode)}</td>
                                                            <td>{row.ip_address || "N/A"}</td>
                                                            <td>{formatJoinAt(row.join_at)}</td>
                                                            <td>{row.connected_seconds ?? 0}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={7} className="text-center">
                                                            No records
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PujaBookingView;
